/**
 * localStorage utility functions
 * Handles data persistence with serialization/deserialization
 */

const STORAGE_VERSION = '1.0.0'
const VERSION_KEY = 'axioma_storage_version'
const STORAGE_PREFIX = 'axioma_'

/**
 * Storage keys for different node types
 */
export const StorageKeys = {
  USERS: `${STORAGE_PREFIX}users`,
  WORKSPACES: `${STORAGE_PREFIX}workspaces`,
  PROJECTS: `${STORAGE_PREFIX}projects`,
  TASKS: `${STORAGE_PREFIX}tasks`,
  MESSAGES: `${STORAGE_PREFIX}messages`,
  DOCUMENTS: `${STORAGE_PREFIX}documents`,
  AUTH_TOKEN: `${STORAGE_PREFIX}auth_token`,
  CURRENT_USER: `${STORAGE_PREFIX}current_user`,
  INITIALIZED: `${STORAGE_PREFIX}initialized`,
} as const

/**
 * Simulate network delay for realistic API behavior
 */
export async function simulateNetworkDelay(ms: number = 100): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Check storage version and initialize if needed
 */
export function checkStorageVersion(): boolean {
  const currentVersion = localStorage.getItem(VERSION_KEY)
  if (currentVersion !== STORAGE_VERSION) {
    clearAllStorage()
    localStorage.setItem(VERSION_KEY, STORAGE_VERSION)
    return false
  }
  return true
}

/**
 * Check if storage has been initialized with seed data
 */
export function isStorageInitialized(): boolean {
  return localStorage.getItem(StorageKeys.INITIALIZED) === 'true'
}

/**
 * Mark storage as initialized
 */
export function markStorageInitialized(): void {
  localStorage.setItem(StorageKeys.INITIALIZED, 'true')
}

/**
 * Get item from localStorage with deserialization
 */
export function getItem<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key)
    if (!item) return null

    const parsed = JSON.parse(item)
    // Convert date strings back to Date objects
    return deserializeDates(parsed)
  } catch (error) {
    console.error(`Error getting item from localStorage: ${key}`, error)
    return null
  }
}

/**
 * Set item in localStorage with serialization
 * Handles circular references by using a replacer function
 */
export function setItem<T>(key: string, value: T): void {
  try {
    const seen = new WeakSet()
    const serialized = JSON.stringify(value, (_key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) {
          return '[Circular]'
        }
        seen.add(val)
      }
      return val
    })
    localStorage.setItem(key, serialized)
  } catch (error) {
    console.error(`Error setting item in localStorage: ${key}`, error)
    throw new Error(`Failed to save data: ${error}`)
  }
}

/**
 * Remove item from localStorage
 */
export function removeItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing item from localStorage: ${key}`, error)
  }
}

/**
 * Clear all Axioma data from localStorage
 */
export function clearAllStorage(): void {
  Object.values(StorageKeys).forEach((key) => {
    localStorage.removeItem(key)
  })
  localStorage.removeItem(VERSION_KEY)
}

/**
 * Get all items of a specific type
 */
export function getAll<T>(key: string): T[] {
  const items = getItem<T[]>(key)
  return items || []
}

/**
 * Set all items of a specific type
 */
export function setAll<T>(key: string, items: T[]): void {
  setItem(key, items)
}

/**
 * Check localStorage capacity
 */
export function checkStorageCapacity(): { available: boolean; used: number; total: number } {
  try {
    const total = 5 * 1024 * 1024 // 5MB typical limit
    let used = 0

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const item = localStorage.getItem(key)
        used += key.length + (item?.length || 0)
      }
    }

    return {
      available: used < total * 0.9, // 90% threshold
      used,
      total,
    }
  } catch (error) {
    console.error('Error checking storage capacity', error)
    return { available: false, used: 0, total: 0 }
  }
}

/**
 * Recursively convert date strings to Date objects
 */
function deserializeDates<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj

  if (typeof obj === 'string') {
    // Check if string is an ISO date format
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/
    if (isoDatePattern.test(obj)) {
      return new Date(obj) as T
    }
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deserializeDates(item)) as T
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = deserializeDates((obj as Record<string, unknown>)[key])
      }
    }
    return result as T
  }

  return obj
}

/**
 * Export all data for backup
 */
export function exportAllData(): Record<string, unknown> {
  const data: Record<string, unknown> = {}
  Object.entries(StorageKeys).forEach(([name, key]) => {
    data[name] = getItem(key)
  })
  return data
}

/**
 * Import data from backup
 */
export function importAllData(data: Record<string, unknown>): void {
  Object.entries(StorageKeys).forEach(([name, key]) => {
    if (data[name]) {
      setItem(key, data[name])
    }
  })
}
